/**
 * Authentication Service
 * 
 * Handles user authentication using Firebase Authentication.
 * Provides login, logout, and session management functionality.
 */

import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  User as FirebaseUser,
  UserCredential,
  updatePassword,
} from 'firebase/auth';
import { auth } from '../firebase/firebase';

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: 'admin' | 'user';
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResult {
  success: boolean;
  user?: User;
  error?: string;
}

export type AuthStateCallback = (user: User | null) => void;
export type Unsubscribe = () => void;

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<User> {
  const userCredential: UserCredential = await signInWithEmailAndPassword(
    auth,
    credentials.email,
    credentials.password
  );

  const user: User = {
    uid: userCredential.user.uid,
    email: userCredential.user.email,
    displayName: userCredential.user.displayName,
    role: 'admin',
  };

  return user;
}

/**
 * Logout the current user
 */
export async function logout(): Promise<void> {
  await signOut(auth);
}

/**
 * Get the current authenticated user
 */
export function getCurrentUser(): User | null {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) return null;

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    role: 'admin',
  };
}

/**
 * Subscribe to authentication state changes
 */
export function onAuthStateChangedCallback(callback: AuthStateCallback): Unsubscribe {
  return onAuthStateChanged(auth, (firebaseUser: FirebaseUser | null) => {
    if (firebaseUser) {
      const user: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        role: 'admin',
      };
      callback(user);
    } else {
      callback(null);
    }
  });
}

/**
 * Get user role from Firestore (placeholder for future implementation)
 */
export async function getUserRole(_uid: string): Promise<'admin' | 'user'> {
  return 'admin';
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return auth.currentUser !== null;
}

/**
 * Get user ID
 */
export function getUserId(): string | null {
  return auth.currentUser?.uid || null;
}

/**
 * Get user email
 */
export function getUserEmail(): string | null {
  return auth.currentUser?.email || null;
}

/**
 * Send password reset email to user
 * @param email - User's email address
 * @throws Error with user-friendly message if operation fails
 */
async function sendPasswordResetEmail(email: string): Promise<void> {
  try {
    await firebaseSendPasswordResetEmail(auth, email);
  } catch (error: any) {
    const errorCode = error?.code || 'unknown';
    const errorMessage = getAuthErrorMessage(errorCode);
    throw new Error(errorMessage);
  }
}

/**
 * Change the password of the current user
 */
async function changePassword(newPassword: string): Promise<void> {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) {
    throw new Error('No hay ningún usuario autenticado.');
  }
  try {
    await updatePassword(firebaseUser, newPassword);
  } catch (error: any) {
    const errorCode = error?.code || 'unknown';
    if (errorCode === 'auth/requires-recent-login') {
      throw new Error('Esta operación es sensible y requiere que hayas iniciado sesión recientemente. Por favor, cierra sesión e inicia sesión de nuevo para cambiar tu contraseña.');
    }
    throw new Error(error.message || 'Error al cambiar la contraseña.');
  }
}

/**
 * Map Firebase auth error codes to user-friendly messages
 */
function getAuthErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'El correo electrónico no es válido.';
    case 'auth/user-disabled':
      return 'Esta cuenta ha sido deshabilitada.';
    case 'auth/user-not-found':
      return 'No existe una cuenta con este correo electrónico.';
    case 'auth/wrong-password':
      return 'La contraseña es incorrecta.';
    case 'auth/too-many-requests':
      return 'Demasiados intentos. Por favor, inténtalo más tarde.';
    case 'auth/network-request-failed':
      return 'Error de conexión. Por favor, verifica tu conexión a internet.';
    case 'auth/invalid-credential':
      return 'Credenciales inválidas. Por favor, verifica tu correo y contraseña.';
    default:
      return 'Error al enviar el correo de recuperación. Por favor, inténtalo de nuevo.';
  }
}

// Export as an object for consistency with other services
export const authService = {
  login,
  logout,
  getCurrentUser,
  onAuthStateChanged: onAuthStateChangedCallback,
  getUserRole,
  isAuthenticated,
  getUserId,
  getUserEmail,
  sendPasswordResetEmail,
  changePassword,
  getAuthErrorMessage,
};